import {
  json,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction,
  type LoaderFunctionArgs,
} from '@remix-run/node';
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData } from '@remix-run/react';
import { withSentry } from '@sentry/remix';
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react';

import { ClientHintCheck, getHints } from './components/client-hints.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { EpicToaster } from './components/toaster.tsx';
import fontStylesheetUrl from './styles/font.css?url';
import tailwindStyleSheetUrl from './styles/tailwind.css?url';
import { csrf } from './utils/csrf.server.ts';
import { getClientEnv } from './utils/env.server.ts';
import { combineHeaders, getDomainUrl } from './utils/misc.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { makeTimings /*time*/ } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';

export const links: LinksFunction = () => {
  return [
    // Preload CSS as a resource to avoid render blocking
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' as const },
    {
      href: 'https://fonts.googleapis.com/css2?family=Arvo:wght@700&display=swap',
      rel: 'stylesheet',
    },
    {
      href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      rel: 'stylesheet',
    },
    { rel: 'preload', href: fontStylesheetUrl, as: 'style' },
    { rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
    { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
    {
      rel: 'alternate icon',
      type: 'image/png',
      href: '/favicons/favicon-32x32.png',
    },
    { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
    { rel: 'manifest', href: '/site.webmanifest' },
    { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: '/favicons/favicon-dark.svg',
      media: '(prefers-color-scheme: dark)',
    },
    { rel: 'stylesheet', href: fontStylesheetUrl },
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: MetaFunction = () => {
  return [{ title: 'Eclipse' }, { name: 'description', content: 'Manages team retrospectives.' }];
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
  };
  return headers;
};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings('root loader');
  // const session = await time(() => requireSession(args), {
  //   timings,
  //   type: 'requireSession',
  //   desc: 'requireSession in root',
  // });

  const { toast, headers: toastHeaders } = await getToast(request);
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken(request.headers);

  return json(
    {
      // session,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        session: {},
      },
      ENV: getClientEnv(),
      toast,
      csrfToken,
    },
    {
      headers: combineHeaders(
        new Headers({ 'Server-Timing': timings.toString() }),
        toastHeaders,
        csrfCookieHeader ? { 'set-cookie': csrfCookieHeader } : null,
      ),
    },
  );
}

function Document({
  children,
  nonce,
  env,
}: {
  children: React.ReactNode;
  nonce: string;
  env?: Record<string, string>;
}) {
  return (
    <html lang="en">
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <Links />
      </head>
      <body className="font-base bg-zinc-900 font-sans font-normal leading-relaxed">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

function App() {
  const nonce = useNonce();
  const data = useLoaderData<typeof loader>();

  return (
    <AuthenticityTokenProvider token={data.csrfToken}>
      <Document nonce={nonce} env={data.ENV}>
        <Outlet />
        <EpicToaster toast={data.toast} />
      </Document>
    </AuthenticityTokenProvider>
  );
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
}

export default withSentry(App);
